import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/AppShell";
import { Spinner } from "@/components/Gate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CourseForm } from "@/pages/admin/CourseForm";
import { errMsg, money, useApi } from "@/lib/api";

export const STATUS_CLS = { draft: "bg-slate-200 text-slate-800", in_review: "bg-amber-500 text-slate-950", published: "bg-emerald-700 text-white", archived: "bg-slate-600 text-white" };

export default function AdminCourses() {
  const api = useApi();
  const navigate = useNavigate();
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/courses", { params: { status: status === "all" ? "" : status, q } }).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [api, status, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const create = async (body) => {
    const { data } = await api.post("/admin/courses", body);
    toast.success("Draft course created");
    navigate(`/admin/courses/${data.id}`);
  };

  return (
    <div>
      <PageHeader eyebrow="Admin" title="Courses">
        <Button onClick={() => setOpen(true)} className="rounded-full bg-[#222d99] hover:bg-[#1a237e]" data-testid="admin-new-course-btn"><Plus className="mr-1 h-4 w-4" /> New course</Button>
      </PageHeader>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList>
            {["all", "draft", "in_review", "published", "archived"].map((s) => <TabsTrigger key={s} value={s} className="capitalize" data-testid={`admin-courses-tab-${s}`}>{s.replace("_", " ")}</TabsTrigger>)}
          </TabsList>
        </Tabs>
        <div className="relative sm:w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles" className="pl-9" data-testid="admin-course-search" />
        </div>
      </div>
      {!list ? <Spinner /> : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="admin-course-list">
          {list.map((c) => (
            <Link key={c.id} to={`/admin/courses/${c.id}`} className="flex flex-col gap-2 border-b border-slate-100 p-4 last:border-0 hover:bg-slate-50 sm:flex-row sm:items-center" data-testid={`admin-course-row-${c.id}`}>
              <div className="flex-1">
                <p className="font-semibold text-slate-900">{c.title}</p>
                <p className="text-xs text-slate-500">{c.module_count} modules · {c.lesson_count} lessons · {c.level} · {money(c.price, c.currency)}</p>
              </div>
              <Badge className={`w-fit capitalize ${STATUS_CLS[c.status]}`}>{c.status.replace("_", " ")}</Badge>
            </Link>
          ))}
          {!list.length && <p className="p-8 text-center text-sm text-slate-500">No courses here.</p>}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle>New course</DialogTitle></DialogHeader>
          <CourseForm onSubmit={create} submitLabel="Create draft" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
